/**
 * JoinLandingContainer — Landing screen for the group invite flow
 * Route: /groups/join/:gid?inviter=<uid>
 *
 * Fetches a display-only invite preview (groupsGetInvitePreview) and renders the
 * group + host + who's already in. "Join the group" → phone auth.
 */
import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import {
  brand,
  fontSans,
  spacing,
  radii,
  shadows,
} from '../../style/splytBrand';
import useJoinPageChrome from './useJoinPageChrome';
import { getGroupInvitePreview } from '../../API/groups';
import { mapPreviewToView, readInviteParams, buildInviteQuery } from './inviteHelpers';
import { track, EVENTS, SURFACE, INVITE_SOURCE, DOWNLOAD_SOURCE } from '../../analytics/track';
import { detectStore } from '../../analytics/helpers';

// ─── Subcomponents ────────────────────────────────────────────────────────────

function MemberAvatar({ initial, color, size = 44, style = {} }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: color,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontFamily: fontSans,
        fontWeight: 700,
        fontSize: size * 0.42,
        lineHeight: 1,
        flexShrink: 0,
        ...style,
      }}
    >
      <span style={{ transform: 'translateY(-0.5px)' }}>{initial}</span>
    </div>
  );
}

function MemberStack({ people, size = 26, max = 4, ringColor }) {
  const visible = people.slice(0, max);
  const extra = people.length - visible.length;

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
      {visible.map((p, i) => (
        <div
          key={i}
          style={{
            marginLeft: i === 0 ? 0 : -10,
            border: `2.5px solid ${ringColor}`,
            borderRadius: '50%',
            zIndex: visible.length - i,
          }}
        >
          <MemberAvatar initial={p.initial} color={p.color} size={size} />
        </div>
      ))}
      {extra > 0 && (
        <div
          style={{
            marginLeft: -10,
            width: size,
            height: size,
            borderRadius: '50%',
            backgroundColor: brand.marshNeutral,
            color: brand.caviar75,
            border: `2.5px solid ${ringColor}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: fontSans,
            fontWeight: 700,
            fontSize: size * 0.38,
          }}
        >
          +{extra}
        </div>
      )}
    </div>
  );
}

function GroupAvatarBig({ emoji, accent, size = 104 }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.28,
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.5,
        lineHeight: 1,
        boxShadow: `0 14px 36px ${accent}55, 0 2px 4px rgba(0,0,0,0.06)`,
      }}
    >
      {/* Gradient background */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${accent} 0%, ${accent}AA 50%, #fff 130%)`,
        }}
      />
      {/* Radial highlight */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 30% 25%, rgba(255,255,255,0.45) 0%, transparent 55%)',
        }}
      />
      {/* Emoji */}
      <span
        style={{
          position: 'relative',
          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.18))',
        }}
      >
        {emoji}
      </span>
    </div>
  );
}

function Wordmark({ size = 22, color = brand.caviar }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        fontFamily: fontSans,
        fontWeight: 700,
        fontSize: size,
        letterSpacing: '-0.04em',
        color,
      }}
    >
      <svg width={size * 0.95} height={size * 0.95} viewBox="0 0 24 24" fill="none">
        <path
          d="M6 4h8a4 4 0 010 8H10a4 4 0 010 8h-4"
          stroke={color}
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
      splyt
    </div>
  );
}

// Neutral stand-in for the group's accent while the preview loads, so the hero
// does not flash a color it is about to replace.
const SKELETON_ACCENT = '#D9D5CD';

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  // Page wrapper
  root: {
    // svh, matching the wrapper below. `100vh` is the LARGE viewport on iOS
    // Safari (toolbars hidden), so it made the document ~80 to 100px taller
    // than the visible area and the page scrolled for no reason.
    minHeight: '100vh',
    '@supports (height: 100svh)': {
      minHeight: '100svh',
    },
    backgroundColor: brand.marsh,
    fontFamily: fontSans,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },

  // Mobile layout (full-bleed)
  mobileWrapper: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    maxHeight: '100vh',
    // Use CSS custom property for dvh fallback
    '@supports (height: 100dvh)': {
      minHeight: '100dvh',
      maxHeight: '100dvh',
    },
    padding: `${spacing.md}px ${spacing.md + 4}px 0`,
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },

  // Desktop layout (centered card)
  desktopWrapper: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minHeight: '100vh',
      padding: '24px 40px 40px',
    },
  },

  // Desktop header row
  desktopHeader: {
    width: '100%',
    maxWidth: 1080,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 28,
  },

  // Desktop card
  desktopCard: {
    width: '100%',
    maxWidth: 480,
    backgroundColor: brand.surface,
    borderRadius: radii.card,
    padding: 36,
    boxShadow: shadows.card,
    border: `1px solid ${brand.marshNeutral}`,
  },

  // Content area
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    justifyContent: 'center',
    paddingTop: 0,
    overflow: 'auto',
  },

  desktopContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    paddingTop: 4,
  },

  // Hero section
  heroWrapper: {
    position: 'relative',
    marginBottom: spacing.lg,
  },

  inviterOverlay: {
    position: 'absolute',
    bottom: -8,
    right: -10,
    borderRadius: '50%',
  },

  // Eyebrow text
  eyebrow: {
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 1.2,
    color: brand.caviar50,
    marginBottom: 6,
  },

  eyebrowName: {
    fontWeight: 700,
    color: brand.caviar,
  },

  // Group name
  groupName: {
    margin: 0,
    fontWeight: 700,
    letterSpacing: '-0.03em',
    color: brand.caviar,
  },

  groupNameMobile: {
    fontSize: 34,
    lineHeight: 1.05,
  },

  groupNameDesktop: {
    fontSize: 38,
    lineHeight: 1.05,
  },

  // Member count pill
  memberPill: {
    marginTop: 22,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 16px 8px 10px',
    borderRadius: radii.pill,
    border: `1px solid ${brand.marshNeutral}`,
  },

  memberPillMobile: {
    backgroundColor: brand.marsh75,
  },

  memberPillDesktop: {
    backgroundColor: brand.marsh,
  },

  memberPillText: {
    fontWeight: 600,
    fontSize: 13,
    lineHeight: 1,
    color: brand.caviar75,
  },

  // Subtitle
  subtitle: {
    margin: '28px 0 0',
    maxWidth: 320,
    fontWeight: 400,
    fontSize: 15,
    lineHeight: 1.5,
    color: brand.caviar75,
  },

  // Footer
  footer: {
    flexShrink: 0,
    padding: `${spacing.sm}px ${spacing.xxs}px`,
    // Extra padding for mobile browser chrome (toolbar)
    paddingBottom: `calc(${spacing.lg}px + env(safe-area-inset-bottom, 0px))`,
  },

  desktopFooter: {
    marginTop: spacing.lg,
  },

  // Primary CTA button
  primaryButton: {
    width: '100%',
    backgroundColor: brand.caviar,
    color: brand.surface,
    border: 'none',
    borderRadius: radii.pill,
    padding: '16px 22px',
    fontFamily: fontSans,
    fontWeight: 700,
    fontSize: 16,
    lineHeight: 1.1,
    letterSpacing: '-0.005em',
    boxShadow: shadows.primaryButton,
    cursor: 'pointer',
    transition: 'transform 120ms ease',
    '&:hover': {
      transform: 'scale(0.985)',
    },
    '&:active': {
      transform: 'scale(0.98)',
    },
    // The skeleton renders this button before the preview resolves; without a
    // disabled style it looked tappable while doing nothing.
    '&:disabled': {
      backgroundColor: brand.caviar25,
      boxShadow: 'none',
      cursor: 'default',
    },
  },

  // Deep link (mobile only)
  deepLinkWrapper: {
    textAlign: 'center',
    marginTop: spacing.sm,
  },

  deepLink: {
    color: brand.blueberry,
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 1.3,
    textDecoration: 'none',
    borderBottom: `1.5px solid ${brand.blueberry25}`,
    paddingBottom: 1,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    fontFamily: fontSans,
  },

  // Desktop header link
  headerLink: {
    color: brand.blueberry,
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 1.3,
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    borderBottom: `1.5px solid ${brand.blueberry25}`,
    padding: 0,
    paddingBottom: 1,
    cursor: 'pointer',
    fontFamily: fontSans,
  },

  // Loading / unavailable states
  stateWrap: {
    minHeight: '100vh',
    '@supports (height: 100dvh)': { minHeight: '100dvh' },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '0 32px',
  },
  spinner: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    border: `3px solid ${brand.marshNeutral}`,
    borderTopColor: brand.caviar,
    animation: '$spin 800ms linear infinite',
  },
  '@keyframes spin': {
    to: { transform: 'rotate(360deg)' },
  },
  // Name placeholder while the preview is in flight. Pulses rather than spins:
  // the shape it will become is already on screen.
  skeletonTitle: {
    width: 190,
    maxWidth: '70%',
    height: 34,
    marginTop: 2,
    borderRadius: 9,
    backgroundColor: brand.marshNeutral,
    animation: '$pulse 1400ms ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.45 },
  },
  '@media (prefers-reduced-motion: reduce)': {
    skeletonTitle: { animation: 'none' },
  },

  stateEmoji: {
    fontSize: 44,
    marginBottom: 14,
  },
  stateTitle: {
    margin: 0,
    fontWeight: 700,
    fontSize: 24,
    lineHeight: 1.15,
    letterSpacing: '-0.02em',
    color: brand.caviar,
  },
  stateText: {
    margin: '10px 0 0',
    maxWidth: 320,
    fontWeight: 400,
    fontSize: 15,
    lineHeight: 1.5,
    color: brand.caviar75,
  },
}));

// ─── Main Component ───────────────────────────────────────────────────────────

export default function JoinLandingContainer() {
  useJoinPageChrome();
  const classes = useStyles();
  const { gid } = useParams();
  const history = useHistory();
  const location = useLocation();

  const { inviter, inviteCode, utmSource } = readInviteParams(location.search);

  // null = loading; otherwise a view model from mapPreviewToView
  // ({ status: 'ok', ... } | { status: 'unavailable' }).
  const [view, setView] = useState(null);

  useEffect(() => {
    let active = true;
    // Top of the invite funnel — fire once when the landing mounts.
    track(EVENTS.GROUP_INVITE_OPENED, {
      group_id: gid,
      inviter_user_id: inviter,
      invite_id: inviteCode,
      source: INVITE_SOURCE.WEB_LINK,
    });
    getGroupInvitePreview(gid, inviter)
      .then((preview) => {
        if (active) setView(mapPreviewToView(preview));
      })
      .catch((err) => {
        console.error('[JoinLanding] preview failed:', err);
        if (active) setView({ status: 'unavailable' });
      });
    return () => {
      active = false;
    };
  }, [gid, inviter, inviteCode]);

  const query = buildInviteQuery({ inviter, inviteCode, utmSource });

  const handleJoinClick = () => {
    history.push(`/groups/join/${gid}/phone${query}`);
  };

  const handleDeepLinkClick = () => {
    track(EVENTS.APP_DOWNLOAD_CLICKED, {
      store: detectStore(),
      source: DOWNLOAD_SOURCE.GROUP_LANDING,
      surface: SURFACE.GROUP,
      group_id: gid,
      invite_id: inviteCode,
    });
    // Best-effort open for an already-installed app; harmless if unhandled.
    window.location.href = `splyt://groups/join/${gid}`;
  };

  // ─── Loading ──────────────────────────────────────────────────────────────
  // The page's own frame, not a bare spinner: the invitee sees they are being
  // invited to something the moment React mounts, and only the group's name and
  // faces arrive late. Same layout as the loaded state, so nothing jumps when
  // the preview lands.
  if (view === null) {
    return (
      <div className={classes.root}>
        <div className={classes.mobileWrapper}>
          <div className={classes.content}>
            <div className={classes.heroWrapper}>
              <GroupAvatarBig emoji="" accent={SKELETON_ACCENT} size={104} />
            </div>
            <div className={classes.eyebrow}>You&apos;re invited to</div>
            <div className={classes.skeletonTitle} />
            <p className={classes.subtitle}>
              Splyt is the easiest way to share costs with your group. Join with your phone number.
            </p>
          </div>
          <div className={classes.footer}>
            <button className={classes.primaryButton} disabled>
              Join the group
            </button>
          </div>
        </div>
        <div className={classes.desktopWrapper}>
          <div className={classes.desktopHeader}>
            <Wordmark size={22} />
          </div>
          <div className={classes.desktopCard}>
            <div className={classes.desktopContent}>
              <div className={classes.heroWrapper}>
                <GroupAvatarBig emoji="" accent={SKELETON_ACCENT} size={120} />
              </div>
              <div className={classes.eyebrow}>You&apos;re invited to</div>
              <div className={classes.skeletonTitle} />
            </div>
            <div className={classes.desktopFooter}>
              <button className={classes.primaryButton} disabled>
                Join the group
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Unavailable (missing / deleted / error) ───────────────────────────────
  if (view.status !== 'ok') {
    return (
      <div className={classes.root}>
        <div className={classes.stateWrap}>
          <div className={classes.stateEmoji}>🔗</div>
          <h1 className={classes.stateTitle}>This invite isn&apos;t available</h1>
          <p className={classes.stateText}>
            The link may be broken, or the group was deleted. Ask whoever invited
            you for a fresh link.
          </p>
        </div>
      </div>
    );
  }

  // ─── Loaded preview ────────────────────────────────────────────────────────
  const { groupName, emoji, accent, inviter: host, members, memberCount } = view;

  const renderContent = (isMobile) => (
    <>
      {/* Hero: Group avatar with inviter overlay */}
      <div className={classes.heroWrapper}>
        <GroupAvatarBig
          emoji={emoji}
          accent={accent}
          size={isMobile ? 104 : 120}
        />
        {host && (
          <div
            className={classes.inviterOverlay}
            style={{
              border: `3px solid ${isMobile ? brand.marsh : brand.surface}`,
            }}
          >
            <MemberAvatar initial={host.initial} color={host.color} size={40} />
          </div>
        )}
      </div>

      {/* Eyebrow */}
      <div className={classes.eyebrow}>
        {host ? (
          <>
            <span className={classes.eyebrowName}>{host.name}</span> invited you to
          </>
        ) : (
          <>You&apos;re invited to</>
        )}
      </div>

      {/* Group name */}
      <h1
        className={`${classes.groupName} ${
          isMobile ? classes.groupNameMobile : classes.groupNameDesktop
        }`}
      >
        {groupName}
      </h1>

      {/* Member count pill */}
      {members.length > 0 && (
        <div
          className={`${classes.memberPill} ${
            isMobile ? classes.memberPillMobile : classes.memberPillDesktop
          }`}
        >
          <MemberStack
            people={members}
            size={26}
            max={4}
            ringColor={isMobile ? brand.marsh75 : brand.marsh}
          />
          <span className={classes.memberPillText}>
            {memberCount} {memberCount === 1 ? 'person' : 'people'} in the group
          </span>
        </div>
      )}

      {/* Subtitle */}
      <p className={classes.subtitle}>
        Splyt is the easiest way to share costs with your group. Join with your phone number.
      </p>
    </>
  );

  return (
    <div className={classes.root}>
      {/* Mobile layout */}
      <div className={classes.mobileWrapper}>
        <div className={classes.content}>{renderContent(true)}</div>
        <div className={classes.footer}>
          <button className={classes.primaryButton} onClick={handleJoinClick}>
            Join the group
          </button>
          <div className={classes.deepLinkWrapper}>
            <button className={classes.deepLink} onClick={handleDeepLinkClick}>
              Already have Splyt? Open the app
            </button>
          </div>
        </div>
      </div>

      {/* Desktop layout */}
      <div className={classes.desktopWrapper}>
        <div className={classes.desktopHeader}>
          <Wordmark size={22} />
          <button type="button" className={classes.headerLink}>
            Already have Splyt?
          </button>
        </div>
        <div className={classes.desktopCard}>
          <div className={classes.desktopContent}>{renderContent(false)}</div>
          <div className={classes.desktopFooter}>
            <button className={classes.primaryButton} onClick={handleJoinClick}>
              Join the group
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
